import { useState } from "react";
import { useNavigate, Navigate } from "react-router-dom";
import { toast } from "sonner";
import { useAuth, formatApiErrorDetail } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { PasswordInput } from "../components/PasswordInput";
import { Label } from "../components/ui/label";

const BG_IMG = "https://images.unsplash.com/photo-1515703944563-dbcfbf121b3d?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2Njd8MHwxfHNlYXJjaHwxfHxtb2Rlcm4lMjBvZmZpY2UlMjBkYXJrfGVufDB8fHx8MTc4NjYxMjE3NXww&ixlib=rb-4.1.0&q=85";

export default function LoginPage() {
  const { user, login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  if (user && user !== false) {
    return <Navigate to="/menu" replace />;
  }

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      await login(email, password);
      toast.success("Accesso effettuato");
      navigate("/menu", { replace: true });
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-canvas flex">
      <div className="hidden lg:block lg:w-1/2 relative">
        <img src={BG_IMG} alt="AREAPAQ" className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 bg-black/60" />
        <div className="absolute bottom-12 left-12 right-12">
          <p className="text-xs tracking-[0.3em] uppercase text-white/50 mb-3">Portale aziendale</p>
          <h2 className="font-heading text-4xl font-bold tracking-tighter text-white leading-none">
            Un unico accesso.<br />Tutti i tuoi strumenti.
          </h2>
        </div>
      </div>

      <div className="w-full lg:w-1/2 flex items-center justify-center p-8 sm:p-12">
        <div className="w-full max-w-md animate-fade-up">
          <p className="text-xs tracking-[0.3em] uppercase text-white/40 mb-2">Benvenuto</p>
          <h1 className="font-heading text-4xl sm:text-5xl font-bold tracking-tighter text-white mb-2">
            AREAPAQ
          </h1>
          <p className="text-sm text-slate-400 mb-10">Il nostro mondo</p>

          <form onSubmit={handleSubmit} className="space-y-6" data-testid="login-form">
            <div className="space-y-2">
              <Label htmlFor="email" className="text-xs tracking-[0.2em] uppercase text-white/60">Email</Label>
              <Input
                id="email"
                type="email"
                required
                data-testid="login-email-input"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="nome@areapaq.it"
                className="h-12 bg-transparent border-white/10 text-white placeholder:text-white/25 focus:border-white/40 focus-visible:ring-1 focus-visible:ring-white/40"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="password" className="text-xs tracking-[0.2em] uppercase text-white/60">Password</Label>
              <PasswordInput
                id="password"
                required
                data-testid="login-password-input"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="••••••••"
                className="h-12 bg-transparent border-white/10 text-white placeholder:text-white/25 focus:border-white/40 focus-visible:ring-1 focus-visible:ring-white/40"
              />
            </div>

            {error && (
              <p data-testid="login-error" className="text-sm text-red-500 border border-red-500/30 bg-red-500/10 rounded-md px-4 py-3">
                {error}
              </p>
            )}

            <Button
              type="submit"
              disabled={loading}
              data-testid="login-submit-button"
              className="w-full h-12 bg-white text-black hover:bg-gray-200 rounded-full font-semibold tracking-wide text-sm"
            >
              {loading ? "Accesso in corso..." : "Accedi"}
            </Button>
          </form>

          <p className="mt-10 text-xs text-white/30">
            Non hai un account? Contatta l'amministratore per la registrazione.
          </p>
        </div>
      </div>
    </div>
  );
}
